import { useState, type ImgHTMLAttributes } from 'react';
import { urlForSanityImage, type SanityImage as SanityImageType } from '@/lib/sanity';
import { cn } from '@/lib/cn';

/**
 * Renders a Sanity image. Accepts either:
 *  - `url` — a precomputed CDN URL from the backend (preferred — backend uses the
 *    same url builder and already applied crop/width params). This skips client
 *    asset-ref parsing entirely and is always accurate.
 *  - `image` — a raw Sanity image object (asset._ref / alt). Useful when the
 *    frontend needs to request a different size than the backend precomputed.
 */
interface SanityImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  url?: string | null;
  image?: SanityImageType | null;
  width: number;
  height?: number;
  alt: string;
  eager?: boolean;
}

export function SanityImage({
  url,
  image,
  width,
  height,
  alt,
  eager,
  className,
  ...rest
}: SanityImageProps) {
  const [loaded, setLoaded] = useState(false);
  const resolved =
    url ??
    urlForSanityImage(image, {
      width: width * 2,
      height: height ? height * 2 : undefined,
      fit: 'crop',
    });

  if (!resolved) {
    return (
      <div
        className={cn('bg-sunken', className)}
        style={{ width, height: height ?? 'auto' }}
        aria-hidden
      />
    );
  }

  return (
    <img
      src={resolved}
      alt={alt}
      width={width}
      height={height}
      loading={eager ? 'eager' : 'lazy'}
      onLoad={() => setLoaded(true)}
      className={cn(
        'block transition-opacity duration-base',
        loaded ? 'opacity-100' : 'opacity-0',
        className
      )}
      {...rest}
    />
  );
}
